import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button/Button';
import { Client } from 'src/features/clients/client-data-types';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import { useQuery } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { clientsActions, clientsSelectors } from 'src/features/clients/redux/clients-slice';
import { QueryOptions, UserInfo } from 'src/utils/types';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import Chip from '@mui/material/Chip/Chip';
import IconButton from '@mui/material/IconButton/IconButton';
import { Edit } from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress/LinearProgress';
import { GridToolbar } from '@mui/x-data-grid/components/toolbar/GridToolbar';
import { getClients } from 'src/features/clients/api';
import { useEffect } from 'react';
import { GridPaginationModel } from '@mui/x-data-grid/models/gridPaginationProps';
import { GridRowId } from '@mui/x-data-grid/models/gridRows';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import AppRoutes from 'src/features/router/data/app-routes';
import { useSnackbar } from 'notistack';
import { ErrorStatusCode } from 'src/features/error/ErrorStatusCode';

const ClientsPage: React.FC = (userInfo: UserInfo) => {
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const paginationModel = useAppSelector(clientsSelectors.paginationModel);
    const searchFilter = useAppSelector(clientsSelectors.searchFilter);
    const sortModel = useAppSelector(clientsSelectors.sortModel);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const mapPageToNextCursor = React.useRef<{ [page: number]: GridRowId }>({});
    const queryOptions = React.useMemo<QueryOptions>(
        () => ({
            cursor: mapPageToNextCursor.current[paginationModel.page - 1],
            pageSize: paginationModel.pageSize,
            searchFilter: searchFilter,
            sortModel: sortModel,
        }),
        [paginationModel, searchFilter, sortModel],
    );

    const clientsQuery = useQuery({
        queryKey: [`clients`, paginationModel],
        queryFn: async () => await getClients(queryOptions, userInfo.token!),
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: false,
    });

    useEffect(() => {
        if (clientsQuery.isFetched) {
            if (!clientsQuery.data) {
                enqueueSnackbar(t(ErrorStatusCode[ErrorStatusCode.getClientsError]), {
                    variant: 'error',
                });
                return;
            }
            if (clientsQuery.data!.errorStatusCode) {
                enqueueSnackbar(t(ErrorStatusCode[clientsQuery.data!.errorStatusCode]), {
                    variant: 'error',
                });
                return;
            }
        }
    }, [clientsQuery.isFetched]);

    useEffect(() => {
        dispatch(clientsActions.resetPaginationModel());
    }, []);

    useEffect(() => {
        clientsQuery.refetch();
    }, [paginationModel, searchFilter, sortModel]);

    useEffect(() => {
        if (!clientsQuery.isLoading && clientsQuery?.data?.response?.pageInfo.endCursor) {
            mapPageToNextCursor.current[paginationModel.page] =
                clientsQuery?.data?.response?.pageInfo.endCursor;
        }
    }, [clientsQuery.isLoading, clientsQuery?.data?.response?.pageInfo.endCursor]);

    const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
        if (newPaginationModel.pageSize !== paginationModel.pageSize) {
            dispatch(clientsActions.setPaginationModel({ ...newPaginationModel, page: 0 }));
            return;
        }

        if (
            newPaginationModel.page === 0 ||
            mapPageToNextCursor.current[newPaginationModel.page - 1]
        ) {
            dispatch(clientsActions.setPaginationModel(newPaginationModel));
        }
    };

    const handleSearchFilterChange = (searchFilter: GridFilterModel) => {
        if (searchFilter.quickFilterValues && searchFilter.quickFilterValues?.length > 0) {
            dispatch(clientsActions.setSearchFilter(searchFilter?.quickFilterValues[0]));
        } else {
            dispatch(clientsActions.setSearchFilter(''));
        }
    };

    const handleSortModelChange = (sortModel: GridSortModel) => {
        if (sortModel.length > 0) {
            dispatch(
                clientsActions.setSortModel({
                    field: sortModel[0].field,
                    sort: sortModel[0].sort as 'asc' | 'desc',
                }),
            );
        } else {
            dispatch(clientsActions.setSortModel(null));
        }
    };

    const handleOnIconClick = (e: any, client: Client, routePath: string) => {
        e.stopPropagation();
        dispatch(clientsActions.setSelectedClient(client));
        const path = generatePath(routePath, { clientId: client.id });
        return navigate(path, { state: { from: '/' } });
    };

    const handleOnClickAdd = () => {
        const path = generatePath(AppRoutes.addClientPath);
        return navigate(path, { state: { from: '/' } });
    };

    const columns: GridColDef<Client>[] = [
        {
            field: 'id',
            headerName: 'id',
            sortable: false,
            flex: 0,
            disableColumnMenu: true,
            width: 350,
        },
        {
            field: 'hosts',
            headerName: t('hosts'),
            flex: 1,
            align: 'left',
            sortable: false,
            minWidth: 350,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{ display: 'flex', flexWrap: 'wrap', maxHeight: 65, overflowY: 'auto' }}
                    >
                        {params.row.hosts.map((host) => {
                            return (
                                <Chip
                                    key={crypto.randomUUID()}
                                    label={host}
                                    size="small"
                                    style={{
                                        marginRight: '3px',
                                        marginBottom: '3px',
                                    }}
                                />
                            );
                        })}
                    </Box>
                );
            },
        },
        {
            field: 'cui',
            headerName: t('cui'),
            flex: 0.2,
            align: 'left',
            sortable: true,
            minWidth: 120,
            maxWidth: 180,
            disableColumnMenu: true,
            renderCell: (params) => {
                if (params.row.cui) {
                    return params.row.cui;
                }

                return '-';
            },
        },
        {
            field: 'createdAt',
            headerName: t('createdAt'),
            flex: 0.2,
            align: 'left',
            sortable: true,
            minWidth: 150,
            maxWidth: 250,
            disableColumnMenu: true,
            renderCell: (params) => {
                if (params.row.createdAt) {
                    const date = new Date(params.row.createdAt);
                    if (!isNaN(date.getTime())) {
                        const options: Intl.DateTimeFormatOptions = {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                        };
                        return date.toLocaleString(i18n.language, options);
                    }
                }

                return 'N/A';
            },
        },
        {
            field: 'favLockerEnabled',
            headerName: '',
            flex: 0.2,
            sortable: false,
            disableColumnMenu: true,
            minWidth: 161,
            renderCell: (params) => {
                return (
                    <>
                        {params.row.favouriteLockerEnabled ? (
                            <Chip
                                label={t('favouriteLockerEnabled')}
                                size="small"
                                color="success"
                                style={{
                                    marginRight: '3px',
                                    marginBottom: '3px',
                                }}
                            />
                        ) : (
                            <></>
                        )}
                    </>
                );
            },
        },
        {
            field: 'editButton',
            headerName: '',
            sortable: false,
            disableColumnMenu: true,
            flex: 0.2,
            align: 'center',
            maxWidth: 40,
            renderCell: (params) => {
                return (
                    <IconButton
                        onClick={(e) => handleOnIconClick(e, params.row, AppRoutes.viewClientPath)}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                );
            },
        },
    ];

    return (
        <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
        >
            <h1>{t('clients')}</h1>
            <Box pt={2}>
                <Button onClick={handleOnClickAdd}>{t('addClient')}</Button>
            </Box>

            <DataGrid
                getRowId={() => crypto.randomUUID()}
                autoHeight
                rowHeight={65}
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                isRowSelectable={() => false}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                rows={clientsQuery.data?.response?.nodes ? clientsQuery.data?.response?.nodes : []}
                columns={columns}
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                onFilterModelChange={handleSearchFilterChange}
                onSortModelChange={handleSortModelChange}
                rowCount={
                    clientsQuery.data?.response?.totalCount
                        ? clientsQuery.data?.response.totalCount
                        : 0
                }
                slots={{
                    loadingOverlay: LinearProgress,
                    toolbar: GridToolbar,
                }}
                slotProps={{
                    toolbar: {
                        csvOptions: { disableToolbarButton: true },
                        printOptions: { disableToolbarButton: true },
                        showQuickFilter: true,
                        quickFilterProps: {
                            className: 'large-input',
                            variant: 'outlined',
                            debounceMs: 500,
                            placeholder: t('search'),
                        },
                    },
                }}
                loading={clientsQuery.status === 'loading'}
                sx={{ minWidth: '80%', boxShadow: '10', borderRadius: '1', border: '0' }}
            />
        </Box>
    );
};

export default ClientsPage;
